import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';

import { UseOutletContextPayroll } from './Payroll';
import PayrollAccessDenied from './PayrollAccessDenied';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { Employee, EmployeePagination } from '../../../models/employee.model';
import { EmployeeDA } from '../../../services/admin/employee.service';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { usePaginationUrl } from '../../../hooks/usePaginationUrl';
import AppPagination from '../../../components/element/Pagination';
import AppPreloader from '../../../components/Preloader';

export interface EmployeeListPageProps {}

const EmployeeListPage: React.FunctionComponent<EmployeeListPageProps> = () => {
  const { setRoute, companyForUser } = UseOutletContextPayroll();
  const { lang } = StateLanguage();
  const { page: urlPage, setPage: setUrlPage, search: urlSearch, setSearch: setUrlSearch } = usePaginationUrl();

  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [pagination, setPagination] = useState<EmployeePagination>({ page: 1, limit: 20, total: 0, pages: 1 });
  const [filter, setFilter] = useState({ status: '' });
  const [accessDenied, setAccessDenied] = useState(false);
  const [searchText, setSearchText] = useState(urlSearch);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadEmployees = async (id_company: string, page: number, searchTerm: string, status: string) => {
    const response = await EmployeeDA.getEmployees(id_company, searchTerm, status, page, pagination.limit);

    if (response.status === 0) return; // cancelled

    if (response.status === 200) {
      const data = response.data;
      const items: Employee[] = Array.isArray(data) ? data : (data?.items ?? []);
      const pag = data?.pagination ?? { page, limit: pagination.limit, total: items.length, pages: 1 };

      setEmployees(items);
      setPagination(pag);
      setAccessDenied(false);
    } else if (response.status === 403 && response.data?.code === 'PAYROLL_ACCESS_DENIED') {
      setAccessDenied(true);
      setEmployees([]);
      setPagination({ page: 1, limit: pagination.limit, total: 0, pages: 1 });
    } else {
      setEmployees([]);
      setPagination({ page: 1, limit: pagination.limit, total: 0, pages: 1 });
    }
  };

  const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    searchTimerRef.current = setTimeout(() => {
      setUrlSearch(value);
      if (companyForUser) {
        loadEmployees(companyForUser.id, 1, value, filter.status);
      }
    }, 300);
  };

  const handleStatusFilter = (status: string) => {
    setFilter({ ...filter, status });
    if (companyForUser) {
      setUrlPage(1);
      loadEmployees(companyForUser.id, 1, searchText, status);
    }
  };

  const loadPagination = (page: number, newLimit?: number) => {
    setUrlPage(page);
    if (companyForUser) {
      loadEmployees(companyForUser.id, page, searchText, filter.status);
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active': return 'badge-light-success';
      case 'draft': return 'badge-light-warning';
      case 'terminated': return 'badge-light-danger';
      default: return 'badge-light-primary';
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'active': return lang.labels.employeeStatusActive;
      case 'draft': return lang.labels.employeeStatusDraft;
      case 'terminated': return lang.labels.employeeStatusTerminated;
      default: return status;
    }
  };

  useEffect(() => {
    if (companyForUser) {
      setFilter({ status: '' });
      setSearchText(urlSearch);
      setEmployees(null);
      loadEmployees(companyForUser.id, urlPage, urlSearch, '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser]);

  useEffect(() => {
    setRoute({path: {root: lang.labels.payroll, branch: lang.labels.employees}, company: false})

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (accessDenied) {
    return <PayrollAccessDenied />;
  }

  return (
    <div className="w-100 h-100">
      { employees
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <div className="d-flex align-items-center position-relative">
                <span className="svg-icon svg-icon-1 position-absolute ms-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                    <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                  </svg>
                </span>
                <input className="form-control w-350px ps-14" type="text" autoComplete="off" placeholder={lang.labels.searchEmployee} value={searchText} onChange={handleChangeSearch} />
              </div>
            </div>
            <div className="card-toolbar">
              <div className="d-flex justify-content-end align-items-center">
                <Dropdown>
                  <Dropdown.Toggle variant={`btn ${filter.status ? "btn-primary" : "btn-light-primary"} me-2`}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M19.0759 3H4.72777C3.95892 3 3.47768 3.83148 3.86067 4.49814L8.56967 12.6949C9.17923 13.7559 9.5 14.9582 9.5 16.1819V19.5072C9.5 20.2189 10.2223 20.7028 10.8805 20.432L13.8805 19.1977C14.2553 19.0435 14.5 18.6783 14.5 18.273V13.8372C14.5 12.8089 14.8171 11.8056 15.408 10.964L19.8943 4.57465C20.3596 3.912 19.8856 3 19.0759 3Z" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.filter}
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-300px w-md-325px" align="end">
                    <div className="mt-5 mx-5">
                      <div className="fs-5 text-dark fw-bolder">{lang.labels.filterOptions}</div>
                    </div>
                    <div className="separator border-gray-200"></div>
                    <div className="mt-3 m-5">
                      <div className="mb-5">
                        <label className="form-label fs-7">{lang.labels.status}:</label>
                        <select className="form-select form-select-sm" value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}>
                          <option value="">{lang.labels.allStatuses}</option>
                          <option value="active">{lang.labels.employeeStatusActive}</option>
                          <option value="draft">{lang.labels.employeeStatusDraft}</option>
                          <option value="terminated">{lang.labels.employeeStatusTerminated}</option>
                        </select>
                      </div>
                      <div className="d-flex justify-content-end">
                        <Dropdown.Item bsPrefix="btn btn-sm btn-light btn-active-light-primary" onClick={() => handleStatusFilter('')}>{lang.labels.reset}</Dropdown.Item>
                        <Dropdown.Item bsPrefix="btn btn-sm btn-primary ms-3" onClick={() => handleStatusFilter(filter.status)}>{lang.labels.apply}</Dropdown.Item>
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
                <Link to="/main/payroll/employees/create" className="btn btn-primary" title={lang.labels.addEmployee}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                        <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                        <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                      </svg>
                    </span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-50px">{lang.labels.number}</th>
                      <th className="min-w-150px">{lang.labels.name}</th>
                      <th className="min-w-100px">{lang.labels.status}</th>
                      <th className="min-w-100px">{lang.labels.startDate}</th>
                      <th className="min-w-100px">{lang.labels.workCanton}</th>
                      <th className="w-75px text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { employees.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { employees.map((employee, index) => (
                        <tr key={employee.id} className="row-dashed">
                          <td>{employee.employee_number ?? '-'}</td>
                          <td>
                            <Link to={`/main/payroll/employees/view/${employee.id}`} className="fw-bolder text-dark text-hover-primary">
                              {`${employee.contact_snapshot?.first_name ?? ''} ${employee.contact_snapshot?.last_name ?? ''}`.trim() || '-'}
                            </Link>
                          </td>
                          <td>
                            <span className={`badge ${statusBadge(employee.status)}`}>
                              {statusLabel(employee.status)}
                            </span>
                          </td>
                          <td>{employee.current_contract?.start_date ? dateFormat(lang.code, employee.current_contract.start_date) : '-'}</td>
                          <td>{employee.current_contract?.work_canton ?? '-'}</td>
                          <td className="text-end">
                            <Dropdown>
                              <Dropdown.Toggle variant="btn btn-sm btn-light btn-active-light-primary">
                                <div className="d-flex align-items-center">
                                  {lang.labels.actions}
                                  <span className="svg-icon svg-icon-5 me-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                                    </svg>
                                  </span>
                                </div>
                              </Dropdown.Toggle>
                              <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }} variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
                                <Dropdown.Item bsPrefix="menu-item" as={Link} to={`/main/payroll/employees/view/${employee.id}`}>
                                  <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.view}</p>
                                </Dropdown.Item>
                                <Dropdown.Item bsPrefix="menu-item" as={Link} to={`/main/payroll/employees/edit/${employee.id}`}>
                                  <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.edit}</p>
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={6} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination
                showOption={true}
                pagination={{
                  current: pagination.page,
                  total: pagination.total,
                  limit: pagination.limit,
                  pages: Array.from({ length: pagination.pages }, (_, i) => i + 1),
                }}
                loadPagination={loadPagination}
              />
            </div>
          </div>
        </div>
        :
        <AppPreloader />
      }
    </div>
  );
};

export default EmployeeListPage;
